/**
 * Auth abstraction shared by the Privy, dev and "unavailable" providers.
 * `getToken()` returns the bearer token for `Authorization: Bearer <token>`:
 * the Privy access token, or `dev:<handle>` in dev mode.
 */
import { createContext, useContext } from 'react';
import type { AuthMode } from '../env';
import type { TxSigner } from './signer';

export interface AuthApi {
  readonly mode: AuthMode;
  /** The provider finished initialising; `authenticated` is meaningful. */
  readonly ready: boolean;
  readonly authenticated: boolean;
  /** Opens the login UI (Privy modal, dev form, or "Login unavailable"). */
  login(): void;
  logout(): Promise<void>;
  getToken(): Promise<string | null>;
  /**
   * Phase 2 (A-02, A-04, A-07, V-2): signs sponsored transactions with the user's wallets.
   * Null (or absent) when logged out or when this auth mode cannot sign.
   */
  readonly signer?: TxSigner | null;
}

export const AuthContext = createContext<AuthApi | null>(null);

export function useAuth(): AuthApi {
  const v = useContext(AuthContext);
  if (!v) throw new Error('useAuth() outside <AuthProvider>');
  return v;
}
